import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MasterLayout from '../layouts/masterlayout';
import ErrorMessage from '../common/errormessage';
import Spinner from '../common/spinner';
import { useLocation, useNavigate } from 'react-router-dom';
import TableHeader from '../common/table_header';
import routes from '../../routes';
import { getHeaders, handleErrorResponse } from '../../lib/requests';
import PaginationControl from '../common/pagination_control';
import usePaginationAndSorting from '../../hooks/usepaginationandsorting';
import GeneralButton from '../common/button_general';
import GeneralizedTableRow from '../common/table_row';
import { PencilSquareIcon, TrashIcon, PrinterIcon } from '@heroicons/react/24/outline';
import NoResultsFound from '../common/noresultsfound';
import { formatDate } from '../../lib/utils';
import OrderFilterComponent from '../common/filtering/order_filter_list';
import { fetchItemsFromDefaultOrder } from '../../services/order_services';
import { handlePdfFile } from '../../lib/utils';

function LoadingModal({ isOpen, message }) {
  if (!isOpen) return null;
  return (
    <div className="fixed z-50 inset-0 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white p-6 rounded-md shadow-md flex flex-col items-center">
        <Spinner />
        <p className="mt-4 text-gray-700">{message}</p>
      </div>
    </div>
  );
}

function DefaultOrderModal({ isOpen, onClose, onSelect }) {
  if (!isOpen) return null;
  return (
    <div className="fixed z-50 inset-0 overflow-y-auto flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white p-4 rounded-md shadow-md">
        <h2 className="text-lg font-semibold mb-2">Which default order would you like to use?</h2>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {[1, 2, 3, 4].map(num => (
            <button
              key={num}
              onClick={() => onSelect(num)}
              className="px-4 py-2 bg-blue-500 text-white rounded"
            >
              Default Order {num}
            </button>
          ))}
        </div>
        <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">
          Cancel
        </button>
      </div>
    </div>
  );
}

function OrderList() {
  const { page, pageSize, sortBy, handlePageChange, handleSortClick } = usePaginationAndSorting('desc_create_date');

  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingOrders, setIsAddingOrders] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [deliveryId, setDeliveryId] = useState(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchQueryParam = queryParams.get('search');
  const deliveryQueryParam = queryParams.get('delivery_id');
  const clientQueryParam = queryParams.get('client_id');
  const afterDate = queryParams.get('after_date');
  const beforeDate = queryParams.get('before_date');
  const delivery_status = queryParams.get('delivery_status');
  const dateSet = queryParams.get('date_set');
  const addAllOrders = queryParams.get('addAllOrders') === 'true';

  const currentRoute = routes.find(route => route.path === location.pathname);
  const routePageTitle = currentRoute ? currentRoute.pageTitle : '';

  const [searchQuery] = useState(searchQueryParam || '');
  const [deliveryIdState] = useState(deliveryQueryParam || '');
  const [clientId, setClientId] = useState(clientQueryParam || '');

  const [selectedDefaultOrderNumber, setSelectedDefaultOrderNumber] = useState(null);
  const [showDefaultOrderModal, setShowDefaultOrderModal] = useState(false);

  const handleCloseDefaultModal = () => {
    setShowDefaultOrderModal(false);
    const newQueryParams = new URLSearchParams(location.search);
    newQueryParams.delete('addAllOrders');
    navigate({ search: newQueryParams.toString() }, { replace: true });
  };

  const handlePrintOrders = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const apiToken = localStorage.getItem('accessToken');
      const response = await axios.get(`${apiUrl}/deliveries/${deliveryId}/orders/pdf`, {
        responseType: 'blob',
        headers: getHeaders(apiToken)
      });

      const fileName = `delivery_${deliveryId}_orders.pdf`;
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      handlePdfFile(pdfBlob, fileName, 'print');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const fetchOrders = async () => {
    if (!pageSize) return;
    setIsLoading(true);
    try {
      setDeliveryId(deliveryIdState);
      const apiUrl = process.env.REACT_APP_API_URL;
      const apiToken = localStorage.getItem('accessToken');
      const response = await axios.get(`${apiUrl}/orders`, {
        headers: getHeaders(apiToken),
        params: {
          page,
          pageSize,
          ...(sortBy && { order_by: sortBy }),
          ...(searchQuery && { search: searchQuery }),
          ...(deliveryIdState && { delivery_id: deliveryIdState }),
          ...(clientId && { client_id: clientId }),
          ...(afterDate && { after_date: afterDate }),
          ...(beforeDate && { before_date: beforeDate }),
          ...(delivery_status && { delivery_status: delivery_status }),
          ...(dateSet && { date_set: dateSet }),
          exclude_default: true
        },
      });

      setTotalPages(response.data.totalPages);
      setTotalRecords(response.data.total);
      setOrders(response.data.data);
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAndAddDefaultOrders = async (defaultOrderNumber) => {
    setIsAddingOrders(true);
    setIsLoading(true);
    try {
      const apiUrl   = process.env.REACT_APP_API_URL;
      const apiToken = localStorage.getItem('accessToken');
      const headers  = getHeaders(apiToken);

      const { data: delivery } = (
        await axios.get(`${apiUrl}/deliveries/${deliveryIdState}`, { headers })
      ).data;

      const { data: customers } = (
        await axios.get(`${apiUrl}/customers?pageSize=1000`, {
          headers,
          params: { client_id: delivery.client_id, status: 'active' }
        })
      ).data;

      const filteredCustomers = customers.filter(
        c => c.client_id === delivery.client_id && c.status === 'active'
      );

      const existingOrders = (
        await axios.get(`${apiUrl}/orders`, {
          headers,
          params: { delivery_id: deliveryIdState }
        })
      ).data.data;
      const existingOrderIds = new Set(existingOrders.map(o => o.customer_id));

      const defaultItemsByCustomer = await Promise.all(
        filteredCustomers.map(c =>
          fetchItemsFromDefaultOrder(c.customer_id, defaultOrderNumber)
        )
      );

      const ordersToCreate = [];
      filteredCustomers.forEach((customer, idx) => {
        if (existingOrderIds.has(customer.customer_id)) return;

        const defaultItems = defaultItemsByCustomer[idx];
        if (!defaultItems.length) return;

        ordersToCreate.push({
          customer_id:  customer.customer_id,
          delivery_id:  deliveryIdState,
          client_id:    delivery.client_id,
          meal_count:   customer.meal_count ||
                        defaultItems.reduce((t, i) => t + i.quantity, 0) || 1,
          menu_id:      customer.default_menu_id,
          delivery_status: 'scheduled',
          items:        defaultItems
        });
      });

      await Promise.all(
        ordersToCreate.map(o =>
          axios.post(`${apiUrl}/orders`, o, { headers })
        )
      );

      fetchOrders();
    } catch (error) {
      handleErrorResponse(
        error,
        navigate,
        setShowError,
        setErrorMessage
      );
    } finally {
      setIsLoading(false);
      setIsAddingOrders(false);
    }
  };

  const handleSelectDefaultOrderNumber = (orderNumber) => {
    handleCloseDefaultModal();
    setSelectedDefaultOrderNumber(orderNumber);
    fetchAndAddDefaultOrders(orderNumber);
  };

  useEffect(() => {
    if (addAllOrders && !selectedDefaultOrderNumber && !showDefaultOrderModal) {
      setShowDefaultOrderModal(true);
    } else {
      fetchOrders();
    }
  }, [
    addAllOrders, selectedDefaultOrderNumber, showDefaultOrderModal,
    page, pageSize, sortBy, searchQuery, deliveryIdState, clientId,
    afterDate, beforeDate, dateSet, delivery_status
  ]);

  const pageTitle = routePageTitle ? routePageTitle : 'Orders';

  const handleDeleteOrder = async () => {
    setShowConfirmationDialog(false);
    const orderIdToDelete = orderToDelete;
    setOrderToDelete(null);
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const apiToken = localStorage.getItem('accessToken');

      await axios.delete(`${apiUrl}/orders/${orderIdToDelete}`, {
        headers: getHeaders(apiToken)
      });

      fetchOrders();
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    { id: 'simple_id', name: 'ID' },
    {
      id: 'customer_full_name', name: 'Client Name', textColor: 'text-gray-900',
      format: (order) => `${order.customer_first_name} ${order.customer_last_name}`,
      link: (value) => `/clients/${value.customer_id}`,
      sortColumn: 'customer_last_name',
      target: 'edit_client'
    },
    {
      id: 'internal_route', name: 'Internal Route',
      format: (value) => value || ''
    },
    { id: 'client_name', name: 'Provider',
      link: (value) => `/providers/${value.client_id}`,
      target: 'edit_customer'
     },
    {
      id: 'delivery_date', name: 'Delivery Date',
      format: (value) => formatDate(value)
    },
    {
      id: 'delivery_status', name: 'Status',
      format: (value) => value.charAt(0).toUpperCase() + value.slice(1)
    },
    {
      id: 'route_name', name: 'Route',
      format: (value) => value || ''
    },
    {
      id: 'meal_count', name: 'Meals',
      format: (value) => value || 0
    },
    {
      id: 'create_date', name: 'Created',
      format: (value) => formatDate(value)
    },
    {
      id: 'modified_date', name: 'Modified',
      format: (value) => formatDate(value)
    },
    {
      id: 'edit', name: 'Edit', textColor: 'text-custom-blue', tooltip: 'Edit Order',
      link: (value) => `/orders/${value.order_id}`, icon: PencilSquareIcon
    },    
    {
      id: 'delete', name: 'Delete', textColor: 'text-red-500', tooltip: 'Delete Order',
      action: (value) => {
        if (value.is_default_order)
        {
          setShowError(true);
          setErrorMessage('This is a default order for ' + value.customer_first_name + ' ' + value.customer_last_name + ', default orders cannot be deleted. Please edit the customer\'s default order instead.');
        }
        else if (value.delivery_status === 'scheduled' || !value.delivery_status) {
          setShowConfirmationDialog(true);
          setOrderToDelete(value.order_id);
        }
        else {
          setShowError(true);
          setErrorMessage('Order ' + value.simple_id + ' cannot be deleted, only scheduled orders can be deleted.');
        }
      }, icon: TrashIcon
    },
  ];

  return (
    <MasterLayout pageTitle={pageTitle} searchQuery={searchQuery}>
      {showError && <ErrorMessage message={errorMessage} />}

      <div className={`fixed z-50 inset-0 flex items-center justify-center bg-black bg-opacity-40 ${showConfirmationDialog ? '' : 'hidden'}`}>
        <div className="bg-white p-4 rounded-md shadow-md">
          <h2 className="text-lg font-semibold mb-4">Are you sure you want to delete this order?</h2>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowConfirmationDialog(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
            <button onClick={handleDeleteOrder} className="px-4 py-2 bg-red-500 text-white rounded">Delete</button>
          </div>
        </div>
      </div>

      <DefaultOrderModal
        isOpen={showDefaultOrderModal}
        onClose={() => handleCloseDefaultModal()}
        onSelect={handleSelectDefaultOrderNumber}
      />

      <LoadingModal
        isOpen={isAddingOrders}
        message="Adding orders, please wait…"
      />

      <div className="px-2 sm:px-3 lg:px-3">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-2 flex justify-between">
          <h1 className="text-lg font-semibold text-gray-900">{pageTitle}</h1>
          {deliveryId ? (
            <div>
              <GeneralButton type="submit" buttonType="primary" onClick={() => {
                const baseUrl = '/orders/create';
                const urlWithParam = deliveryId ? `${baseUrl}?delivery_id=${deliveryId}` : baseUrl;
                navigate(urlWithParam);
              }}>
                Create Order
              </GeneralButton>
              <GeneralButton type="button" buttonType="secondary" onClick={() => navigate(`/deliveries/${deliveryId}`)}>
                Edit Delivery
              </GeneralButton>
              {orders.length > 0 ? (
                <GeneralButton type="button" buttonType="secondary" onClick={handlePrintOrders}>
                  <PrinterIcon className="h-5 w-5 inline-block mr-1" />
                  Print Orders
                </GeneralButton>
              ) : null}
            </div>
          ) : null}
        </div>
        <OrderFilterComponent onFilterChange={(client) => setClientId(client ? client.client_id : '')} />
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-2">
          <table className="min-w-full divide-y divide-gray-300">
            <TableHeader columns={columns} handleSortClick={handleSortClick} currentSort={sortBy} />
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    <Spinner minHeight={630} />
                  </td>
                </tr>
              ) : orders.length > 0 ? (
                orders.map((order) => (
                  <GeneralizedTableRow key={order.order_id} rowData={order} columns={columns} />
                ))
              ) : (
                <NoResultsFound message={"No " + pageTitle.toLocaleLowerCase() + " found"} minHeight={630} columSpan={columns.length} />
              )}
            </tbody>
          </table>
          {!isLoading ? (
            <PaginationControl
              page={page}
              recordsPerPage={pageSize}
              totalRecords={totalRecords}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          ) : null}
        </div>
      </div>
    </MasterLayout>
  );
}

export default OrderList;
